export function formatSecondsToDDHHMMSS(
  totalSeconds?: number | null,
  opts?: {
    first?: number;
    last?: number;
    defaultValue?: string;
  }
): string {
  const defaultValue = opts?.defaultValue ?? "0s";
  if (totalSeconds == null) {
    return defaultValue;
  }

  const days = Math.floor(totalSeconds / 86400); // 86400 seconds in a day
  const hours = Math.floor((totalSeconds % 86400) / 3600); // 3600 seconds in an hour
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Number.parseInt(`${totalSeconds % 60}`, 10);

  const formatVal = (val: number, suffix: string) => {
    if (!Number.isSafeInteger(val) || val === 0) {
      return "";
    }

    return `${val.toString().padStart(2, "0")}${suffix}`;
  };

  const dd = formatVal(days, "d");
  const hh = formatVal(hours, "h");
  const mm = formatVal(minutes, "m");
  const ss = formatVal(seconds, "s");

  if (opts?.first) {
    const parts = [dd, hh, mm, ss].filter((part) => part !== "");
    return parts.slice(0, opts.first).join(" ").trim() || defaultValue;
  } else if (opts?.last) {
    const parts = [dd, hh, mm, ss].filter((part) => part !== "");
    return parts.slice(-opts.last).join(" ").trim() || defaultValue;
  }

  return `${dd} ${hh} ${mm} ${ss}`.trim() || defaultValue;
}

export function segmentify(interval: number, ...times: number[]) {
  if (times.length === 0) {
    return [];
  }

  const maxTime = Math.max(...times);
  const segmentNo = Math.ceil(maxTime / interval);
  const segmentRanges = Array.from({ length: segmentNo }, (_, i) => ({
    start: i * interval,
    end: (i + 1) * interval,
  }));
  const segments = segmentRanges.map((range) => {
    const count = times.filter(
      (time) => time >= range.start && time < range.end
    ).length;
    return { ...range, count };
  });

  // console.log(`Segmentified times: ${JSON.stringify(segments)}`);
  // console.log(
  //   `Max time: ${maxTime}, Interval: ${interval}, Segments: ${segments.length}`
  // );

  return segments;
}

export function formatSeries(...numbers: number[]) {
  return numbers.map((n, i, arr) =>
    n !== 0 ? n : arr[i + 1] !== 0 || arr[i - 1] !== 0 ? 0 : null
  );
}

export function isToday(date: Date | string | number): boolean {
  const givenDate = new Date(date);
  const today = new Date();

  return (
    givenDate.getDate() === today.getDate() &&
    givenDate.getMonth() === today.getMonth() &&
    givenDate.getFullYear() === today.getFullYear()
  );
}

export function debounce<F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<F>) =>
    new Promise<ReturnType<F>>((resolve) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        resolve(func(...args));
      }, waitFor);
    });
}

export function splitIntoChunks<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

export function wildcardMatch(value: string, rule: string): boolean {
  var escapeRegex = (val: string) =>
    val.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");

  return new RegExp(
    "^" + rule.split("*").map(escapeRegex).join(".*") + "$"
  ).test(value);
}

export function toMilliseconds({
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
}: {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}) {
  return ((days * 24 + hours) * 60 + minutes) * 60 * 1000 + seconds * 1000;
}

export function millisecondsToTimeComponents(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

/**
 *  Divides two numbers safely, returning 0 if the denominator is zero or if either value is null/undefined.
 *
 * @param numerator The numerator of the division.
 * @param denominator The denominator of the division.
 * @returns The result of the division or 0 if invalid.
 */
export function divideSafely(
  numerator?: number | null,
  denominator?: number | null
): number {
  if (numerator == null || denominator == null) {
    return 0;
  } else if (denominator === 0) {
    return 0;
  }
  return numerator / denominator;
}

/**
 * Sums the numbers in the array, ignoring null or undefined values.
 * @param values  The array of numbers to sum.
 * @returns  The sum of the numbers.
 */

export function sum(values: (number | null | undefined)[]): number {
  let total = 0;
  for (const value of values) {
    const num = Number(value);
    if (!isNaN(num)) {
      total += num;
    }
  }
  return total;
}

/**
 * Sums the values obtained by applying the key function to each item in the array.
 * @param items The array of items to sum over.
 * @param key A function that takes an item and returns a number (or null/undefined).
 * @returns The sum of the values returned by the key function.
 */
export function keySum<T>(
  items: T[],
  key: (item: T) => number | null | undefined
): number {
  let total = 0;
  for (const item of items) {
    const value = key(item);
    const num = Number(value);
    if (!isNaN(num)) {
      total += num;
    }
  }
  return total;
}

/**
 * Calculates the average of an array of numbers, ignoring null or undefined values.
 * If the array is empty or contains no valid numbers, returns null.
 *
 * @param data  The array of numbers to average.
 * @returns  The average of the numbers, or null if the array is empty or contains no valid numbers.
 */
export function average(data: (number | null | undefined)[]): number | null {
  if (data.length === 0) return null;
  let total = 0;
  let count = 0;
  for (const value of data) {
    let num = Number(value);
    if (!isNaN(num)) {
      total += num;
      count++;
    }
  }
  return count === 0 ? null : total / count;
}

export function keyAverage<T>(
  items: T[],
  key: (item: T) => number | null | undefined
): number | null {
  const values = [];
  for (const item of items) {
    const value = key(item);
    const num = Number(value);
    if (!isNaN(num)) {
      values.push(num);
    }
  }
  return average(values);
}

/**
 * Calculates the percentage of a number over another.
 *
 * @param part The part value.
 * @param total The total value.
 * @returns The percentage value.
 */
export function percentage(part: number, total: number): number {
  if (total === 0) return 0;
  return (part / total) * 100;
}

export function percentageOfMatchingItems<T>(
  data: T[],
  filter: (item: T) => boolean
): number {
  const filtered = data.filter(filter);
  return percentage(filtered.length, data.length);
}

export function findFirstAndLast<T>(
  items: T[],
  isTarget: (item: T) => boolean
) {
  let first: T | null = null;
  let last: T | null = null;
  let i = 0;
  while (!first && !last && i < items.length) {
    if (!first && isTarget(items[i])) {
      first = items[i];
    }

    const backwardIndex = items.length - 1 - i;
    if (!last && isTarget(items[backwardIndex])) {
      last = items[backwardIndex];
    }

    i++;
  }

  return { first, last };
}
/**
 * Formatta un timestamp in una stringa leggibile
 */

export const formatRelativeTime = (timestamp: number): string => {
  if (timestamp === 0) return "Mai sincronizzato";

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - timestamp;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return "Adesso";
  if (diffMinutes < 60) return `${diffMinutes} minuti fa`;
  if (diffHours < 24) return `${diffHours} ore fa`;
  if (diffDays === 1) return "Ieri";
  if (diffDays < 7) return `${diffDays} giorni fa`;

  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export type DateLike = Date | string | number;

export function toTimestamp(value: DateLike): number {
  return typeof value === "number" ? value : new Date(value).getTime();
}

export const isInTimeRange = (
  { min, max }: { min?: DateLike | null; max?: DateLike | null },
  value?: DateLike | null
): boolean => {
  if (value == null) return false;
  const timestamp = toTimestamp(value);
  const minTimestamp = min != null ? toTimestamp(min) : null;
  const maxTimestamp = max != null ? toTimestamp(max) : null;

  const isMinValid = minTimestamp != null ? timestamp >= minTimestamp : true;
  const isMaxValid = maxTimestamp != null ? timestamp <= maxTimestamp : true;

  return isMinValid && isMaxValid;
};

export function minItem<T>(items: T[], key: (item: T) => number): T | null {
  let minItem: T | null = null;
  let minValue = Infinity;
  for (const item of items) {
    const value = key(item);
    if (value < minValue) {
      minValue = value;
      minItem = item;
    }
  }

  return minItem;
}

export function maxItem<T>(items: T[], key: (item: T) => number): T | null {
  let maxItem: T | null = null;
  let maxValue = -Infinity;

  for (const item of items) {
    const value = key(item);
    if (value > maxValue) {
      maxValue = value;
      maxItem = item;
    }
  }

  return maxItem;
}

/**
 * Forces a value to be an array.
 * If the value is null or undefined, an empty array is returned.
 * If the value is already an array, it is returned as is.
 * Otherwise, the value is wrapped in an array.
 *
 * @param value
 * @returns
 */
export function forceArray<T>(value: T | T[] | null | undefined): T[] {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/**
 *  Forces a value to be a Promise.
 *  If the value is already a Promise, it is returned as is.
 *  Otherwise, the value is wrapped in a resolved Promise.
 *
 * @param value The value to be forced into a Promise.
 * @returns  A Promise that resolves to the given value.
 */
export function forcePromise<T>(value: T | Promise<T>): Promise<T> {
  if (value instanceof Promise) {
    return value;
  }
  return Promise.resolve(value);
}
